// The Pipeline (`#pipeline` across the requisitions this seat reads, `#pipeline/<R>` for one; `?view=table`), DS §1.8,
// DC-31, P-1…P-8: his seven columns, Held staying in its column with its age and reason ("Held remains active with age
// and reason."), the *Passed* strip under the board with each row's disposition reason, the slate's *In HM Review* pill
// with its due date and *Overdue* once late, and the aging line *In stage n days · Active Time*. Cards are never
// dragged. On one requisition its Recruiter ticks qualified candidates into the SelectionBar and submits the
// Candidate slate submission (`?action=slate`); its Hiring Manager opens a decision from an *In HM Review* card
// (`?decide=<aid>`). Nothing here reminds anyone by hand: the configured SLA tiers are the reminders (plan D22).
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ApplicationCard, CandidateListListOutput, CandidateSlateOpenOutput, HrbpApplicationCard, HrbpCandidateList, RequisitionListOutput } from "@/api/tools";
import { candidateList, candidateOpen, candidateSlateOpen, requisitionList } from "@/api/tools";
import { AppError } from "@/api/runtime";
import { isClientRefusal } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  Banner, Button, CARD_ROWS, CardGrid, Due, EmptyState, RecordCard, ErrorStrip, Flex, G1_LABELS, METRIC_TEMPLATES, G6_COLUMN_LABELS, G6_TEMPLATES, HM_REVIEW_STATUS, HM_SELECTION_STATUS, Kanban,
  KanbanCard, NAV_ITEM_LABELS, PIPELINE_STAGES, PageHeader, Pill, RULE_LABELS, SCREEN_STATUS, SLATE_STATUS, Segmented, Select,
  SelectionBar, Stack, Table, Td, TextLink, Th, Tr, formatDate, type PipelineStage,
} from "@/mc";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { usePassReasonChoices, useWithdrawalReasonChoices, wordIn } from "@/views/candidate/options";
import { DecisionSlideOver } from "./DecisionSlideOver";
import { PIPELINE_LABELS, PIPELINE_RULES } from "./labels";
import { COLUMN_OF, boardOf, dueTierOf, everyReadRefused, nameOf, pipelineRows } from "./pipeline";
import { pipelineRefusal } from "./refusals";
import { SlateDialog } from "./SlateDialog";

type Card = ApplicationCard | HrbpApplicationCard;
type Listed = CandidateListListOutput | HrbpCandidateList;

/** One requisition's pipeline and the due dates of its open slates. */
export interface RequisitionPipeline {
  list: Listed;
  slates: Record<number, CandidateSlateOpenOutput>;
}

const OPEN_SLATE = new Set(["submitted", "in_hm_review", "overdue"]);

/** candidate.list for a requisition plus `candidate.slate_open` for each open slate; a refused requisition reads null. */
export async function readPipeline(rid: string, tolerate: boolean): Promise<RequisitionPipeline | null> {
  let list: Listed;
  try {
    list = await candidateList({ requisition_id: rid });
  } catch (error) {
    if (tolerate && error instanceof AppError && isClientRefusal(error)) return null;
    throw error;
  }
  const slates: Record<number, CandidateSlateOpenOutput> = {};
  const open = "batches" in list ? list.batches.filter((b) => OPEN_SLATE.has(String(b["state"]))).map((b) => Number(b["batch_no"])) : [];
  await Promise.all(open.map(async (n) => {
    try {
      slates[n] = await candidateSlateOpen({ requisition_id: rid, batch_no: n });
    } catch { /* a slate this seat may not open draws no due date */ }
  }));
  return { list, slates };
}

export function PipelinePage({ rid }: { rid?: string | undefined }) {
  return <PipelineBody rid={rid} />;
}

/**
 * One requisition's board without the page header: the body of the requisition's Candidates tab
 * (`#requisitions/<R>/candidates`, mounted from views/b3/register.ts through the requisition detail's tab registry).
 * The slate (`?action=slate`) and the Hiring Manager's decision (`?decide=<aid>`) open on the tab's own route. Entering
 * the tab always re-reads the board: the acts that move its cards are made elsewhere (Add candidate on the candidate
 * master, Qualify, Release, Withdrew and Reinstate on the application record), and none of their writes names this
 * board's key, so a cached board would show the requisition as it stood before them.
 */
export function PipelineBoard({ rid }: { rid: string }) {
  const route = useRoute();
  const { can } = useSession();
  const flags = queryOf(route.raw);
  const board = useSettledRead(["pipeline", "board", rid], () => readPipeline(rid, false), { enabled: can("candidate.list"), refetchOnMount: "always" });
  if (!can("candidate.list")) return null;
  if (board.error) {
    return pipelineRefusal(board.error) && isClientRefusal(board.error)
      ? <Banner tone="red">{pipelineRefusal(board.error)}</Banner>
      : <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: board.error.status, refusal_code: board.error.code, request_id: board.error.requestId }} onRetry={() => void board.refetch()}  />;
  }
  if (!board.data) return <Kanban label={NAV_ITEM_LABELS.pipeline} columns={PIPELINE_STAGES.map((stage) => ({ stage }))} loading scroll />;
  // F-185: on the requisition's Candidates tab the board is one list card per stage (the page grammar's card grid)
  return <OneRequisition rid={rid} data={board.data} view="cards" flags={flags} base={`#requisitions/${rid}/candidates`} />;
}

function PipelineBody({ rid }: { rid?: string | undefined }) {
  const route = useRoute();
  const { can, me } = useSession();
  const { register } = useViewState();
  const flags = queryOf(route.raw);
  const view = flags.get("view") === "table" ? "table" : "board";
  const reqs = useSettledRead(["pipeline-requisitions"], () => requisitionList({ page_size: 100 }));
  const offered = pipelineRows(reqs.data?.items ?? []);
  const ids = rid ? [rid] : offered.map((row) => row.id);
  const boards = useSettledRead(["pipeline", ids], () => Promise.all(ids.map((id) => readPipeline(id, !rid))), { enabled: Boolean(rid) || reqs.isSuccess });
  const shown = (boards.data ?? []).filter((one): one is RequisitionPipeline => one !== null);
  useEffect(() => register({ view: "pipeline", ids: rid ? { requisition: rid } : {}, summary: { tab: view, count: shown.length } }), [register, rid, view, shown.length]);

  // His sentence is the Interviewer's; any other seat without the candidate list never reaches a Pipeline at all. A
  // refused read is the same state, never a silent empty board (demo smoke D9).
  const unavailable = me?.roles.includes("interviewer") ? <EmptyState icon="lock">{RULE_LABELS.interviewersNoAccess}</EmptyState> : <NotFound route={route.raw} />;
  if (!can("candidate.list")) return unavailable;
  if (rid && boards.error && isClientRefusal(boards.error)) return unavailable;
  if (!rid && everyReadRefused(boards.data)) return unavailable;
  const one = rid ? shown[0] : undefined;
  const title = one ? `${NAV_ITEM_LABELS.pipeline} · ${one.list.requisition_title}` : NAV_ITEM_LABELS.pipeline;
  const error = boards.error ?? reqs.error;

  return (
    <Stack gap={16}>
      <PageHeader
        title={title}
        meta={rid ? [<TextLink key="r" href={`#requisitions/${rid}/candidates`} >{rid}</TextLink>] : undefined}
        actions={(
          <Flex gap={8} wrap>
            <Select
              sm
              aria-label={NAV_ITEM_LABELS.requisitions}
              placeholder={NAV_ITEM_LABELS.requisitions}
              value={rid ?? ""}
              options={offered.map((row) => ({ value: row.id, label: `${row.id} · ${row.title}` }))}
              onChange={(value) => go(value ? `#pipeline/${value}` : "#pipeline")}
              
            />
            <Segmented
              label={NAV_ITEM_LABELS.pipeline}
              value={view}
              options={[{ value: "board", label: G1_LABELS.board, hs: "pipe.view.board" }, { value: "table", label: G1_LABELS.table, hs: "pipe.view.table" }]}
              onChange={(next) => go(withQuery(rid ? `#pipeline/${rid}` : "#pipeline", { view: next === "table" ? "table" : null }, flags))}
            />
          </Flex>
        )}
      />
      {error ? (
        pipelineRefusal(error) && isClientRefusal(error)
          ? <Banner tone="red">{pipelineRefusal(error)}</Banner>
          : <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: error.status, refusal_code: error.code, request_id: error.requestId }} onRetry={() => void boards.refetch()}  />
      ) : null}
      {rid ? (
        one ? <OneRequisition rid={rid} data={one} view={view} flags={flags} /> : boards.isLoading ? <Kanban label={NAV_ITEM_LABELS.pipeline} columns={PIPELINE_STAGES.map((stage) => ({ stage }))} loading scroll /> : null
      ) : (
        <Board label={NAV_ITEM_LABELS.pipeline} data={shown} view={view} loading={reqs.isLoading || boards.isLoading} reqs={reqs.data} />
      )}
    </Stack>
  );
}

function OneRequisition({ rid, data, view, flags, base = `#pipeline/${rid}` }: {
  rid: string; data: RequisitionPipeline; view: "board" | "table" | "cards"; flags: URLSearchParams; base?: string | undefined;
}) {
  const { can } = useSession();
  const list = data.list;
  const composes = list.projection === "identity" && list.state_archive === "live" && can("candidate.batch_send");
  const qualified = useMemo(() => list.items.filter((card) => card.stage === "qualified"), [list.items]);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const slating = composes && flags.get("action") === "slate";
  // a slate opened from the URL with nothing ticked starts from every qualified candidate (the frame's state)
  const chosen = slating && picked.size === 0 ? new Set(qualified.map((card) => card.id)) : picked;
  const deciding = flags.get("decide");
  const decides = list.projection === "review" && can("candidate.decide_resume");

  return (
    <>
      {list.items.length === 0 ? (
        <EmptyState icon="users">{PIPELINE_RULES.slate}</EmptyState>
      ) : (
        <Board
          label={`${NAV_ITEM_LABELS.pipeline} · ${rid}`}
          data={[data]}
          view={view}
          select={composes ? { picked, onPick: (id, on) => setPicked((now) => { const next = new Set(now); if (on) next.add(id); else next.delete(id); return next; }) } : undefined}
          decide={decides ? (aid) => go(withQuery(base, { decide: aid }, flags)) : undefined}
        />
      )}
      {composes ? (
        <SelectionBar
          selected={qualified.filter((card) => picked.has(card.id)).map((card) => ({ name: nameOf(card), hueKey: card.token }))}
          onClear={() => setPicked(new Set())}
          onSubmit={() => go(withQuery(base, { action: "slate" }, flags))}
          hs={{ submit: "pipe.submit", clear: "pipe.clear" }}
        />
      ) : null}
      {slating ? (
        <SlateDialog
          rid={rid}
          candidates={qualified.filter((card): card is ApplicationCard => chosen.has(card.id) && "recruiter_note" in card)}
          onClose={(sent) => { if (sent) setPicked(new Set()); go(withQuery(base, { action: null }, flags)); }}
        />
      ) : null}
      {deciding && decides ? (
        <DecisionSlideOver
          rid={rid}
          card={list.items.find((card): card is ApplicationCard => card.id === deciding && "recruiter_note" in card) ?? null}
          slate={Object.values(data.slates).find((slate) => slate.rows.some((row) => row.application_id === deciding)) ?? null}
          onClose={() => go(withQuery(base, { decide: null }, flags))}
        />
      ) : null}
    </>
  );
}

interface BoardSelect {
  picked: ReadonlySet<string>;
  onPick: (id: string, on: boolean) => void;
}

function Board({ label, data, view, loading, reqs, select, decide }: {
  label: string; data: readonly RequisitionPipeline[]; view: "board" | "table" | "cards"; loading?: boolean | undefined;
  reqs?: RequisitionListOutput | undefined; select?: BoardSelect | undefined; decide?: ((aid: string) => void) | undefined;
}) {
  const cards = data.flatMap((one) => one.list.items.map((card) => ({ ...card, rid: one.list.requisition_id, slates: one.slates })));
  const { columns, passed } = boardOf(cards);
  if (!loading && reqs && data.length === 0) return <EmptyState icon="users">{PIPELINE_RULES.slate}</EmptyState>;
  if (view === "table") return <PipelineTable cards={cards} loading={loading} />;
  if (view === "cards") {
    // one list card per stage, in the board's order: its first five candidates (the same cards, selection and Advance ·
    // Hold · Pass), then the whole board for the rest; the Passed strip is the last card
    const rid = data[0]?.list.requisition_id ?? "";
    return (
      <CardGrid>
        {PIPELINE_STAGES.map((stage) => {
          const held = columns[stage].filter((card) => card.stage === "held").length;
          return (
            <RecordCard
              key={stage}
              variant="list"
              title={G6_COLUMN_LABELS[stage]}
              count={columns[stage].length}
              pills={held ? <Pill tone="warn">{`${held} ${HM_REVIEW_STATUS.held}`}</Pill> : undefined}
              rows={[]}
              empty={columns[stage].length ? undefined : <span className="mc-rc-s">—</span>}
              all={columns[stage].length > CARD_ROWS ? { href: `#pipeline/${rid}`, what: G6_COLUMN_LABELS[stage], always: true } : undefined}
              
            >
              {columns[stage].length ? (
                <Stack gap={8}>
                  {columns[stage].slice(0, CARD_ROWS).map((card, index) => (
                    <StageCard key={card.id} card={card} index={index} stage={stage} select={stage === "qualified" ? select : undefined} decide={decide} />
                  ))}
                </Stack>
              ) : null}
            </RecordCard>
          );
        })}
        {passed.length ? (
          <RecordCard variant="list" title={HM_REVIEW_STATUS.passed} count={passed.length} rows={[]}>
            <Stack gap={4}>{passed.map((card, index) => <PassedRow key={card.id} card={card} index={index} />)}</Stack>
          </RecordCard>
        ) : null}
      </CardGrid>
    );
  }
  return (
    <Kanban
      label={label}
      scroll
      full
      loading={loading}
      columns={PIPELINE_STAGES.map((stage) => ({
        stage,
        cards: columns[stage].map((card, index) => (
          <StageCard key={card.id} card={card} index={index} stage={stage} select={stage === "qualified" ? select : undefined} decide={decide} />
        )),
        foot: columns[stage].some((card) => card.stage === "held")
          ? <Pill tone="warn">{`${columns[stage].filter((card) => card.stage === "held").length} ${HM_REVIEW_STATUS.held}`}</Pill>
          : undefined,
      }))}
      passed={passed.length ? <Stack gap={4}>{passed.map((card, index) => <PassedRow key={card.id} card={card} index={index} />)}</Stack> : undefined}
    />
  );
}

type BoardCard = Card & { rid: string; slates: Record<number, CandidateSlateOpenOutput>; batch_no?: number | null; held_from_stage?: string | null };

/**
 * The state pills a card carries (DS-NEXT's KanbanCard `pills` array): the slate's *In HM Review* with its due date and
 * *Overdue* once late, and *Pending HM Selection*. The Held decision is the card's stamp, not a pill (`stampOf`).
 */
function pillsOf(card: BoardCard): ReactNode[] {
  const slate = card.batch_no ? card.slates[card.batch_no] : undefined;
  const pills: ReactNode[] = [];
  if (card.stage === "in_review" && slate) {
    const overdue = dueTierOf(slate) === "overdue";
    pills.push(<Pill key="review" tone={overdue ? "red" : "info"}>{overdue ? SLATE_STATUS.overdue : SLATE_STATUS.inHmReview}</Pill>);
    if (slate.due_at) pills.push(<Due key="due" tier={dueTierOf(slate)} phrase={formatDate(slate.due_at)} at={slate.due_at} still />);
  } else if (card.stage === "in_review") {
    pills.push(<Pill key="review" tone="info">{SLATE_STATUS.inHmReview}</Pill>);
  }
  if (card.stage === "screened") pills.push(<Pill key="selection" tone="info">{HM_SELECTION_STATUS.pendingHmSelection}</Pill>);
  return pills;
}

/** The decision stamped on a card (DS-NEXT's KanbanCard `stamp` slot): Held stays in its column with its age and reason. */
function stampOf(card: BoardCard): ReactNode {
  if (card.stage !== "held") return undefined;
  const reason = "hold_reason_label" in card ? card.hold_reason_label : null;
  return <Pill tone="warn">{[HM_REVIEW_STATUS.held, card.held_days != null ? METRIC_TEMPLATES.nDays({ n: card.held_days }) : null, reason].filter(Boolean).join(" · ")}</Pill>;
}

/** The table's status cell: the same pills and stamp as the card. */
function Pills({ card }: { card: BoardCard }) {
  return <>{pillsOf(card)}{stampOf(card)}</>;
}

function StageCard({ card, index, stage, select, decide }: {
  card: BoardCard; index: number; stage: PipelineStage; select?: BoardSelect | undefined; decide?: ((aid: string) => void) | undefined;
}) {
  const slot = `${stage.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}.${index}`;
  return (
    <KanbanCard
      name={nameOf(card)}
      hueKey={card.token}
      href={`#requisitions/${card.rid}/candidates/${card.id}`}
      now={card.rid}
      pills={pillsOf(card)}
      stamp={stampOf(card)}
      aging={card.waiting_business_days}
      select={select ? { checked: select.picked.has(card.id), onChange: (on) => select.onPick(card.id, on), hs: `pipe.card.${slot}.select` } : undefined}
      actions={decide && card.stage === "in_review" ? (
        <Button sm tone="ghost" onClick={() => decide(card.id)} >{PIPELINE_LABELS.hmAdvanceHoldPass}</Button>
      ) : undefined}
      
    />
  );
}

/** A Passed row: the candidate, his status word (Passed, or Withdrawn) and its disposition reason. */
function PassedRow({ card, index }: { card: BoardCard; index: number }) {
  const named = "display_name" in card;
  const withdrawalReasons = useWithdrawalReasonChoices(named && card.stage === "withdrawn");
  const passReasons = usePassReasonChoices(named && card.stage !== "withdrawn");
  const open = useSettledRead(["application", card.id, card.rid], () => candidateOpen({ requisition_id: card.rid, application_id: card.id }), { enabled: named });
  const out = open.data && "decisions" in open.data ? open.data : null;
  const withdrawn = card.stage === "withdrawn";
  const reason = !out ? null
    : withdrawn
      ? wordIn(withdrawalReasons, String(out.withdrawals.at(-1)?.["reason_code"] ?? "") || null)
      : wordIn(passReasons, String([...out.decisions].reverse().find((d) => d["value"] === "pass")?.["reason_code"] ?? (out.disposition?.["reason_code"] ?? "")) || null);
  const column = COLUMN_OF[card.furthest_stage];
  return (
    <Flex gap={8} wrap>
      <TextLink href={`#requisitions/${card.rid}/candidates/${card.id}`} >{nameOf(card)}</TextLink>
      <Pill tone="neutral">{withdrawn ? SCREEN_STATUS.withdrawn : HM_REVIEW_STATUS.passed}</Pill>
      {column ? <span>{G6_COLUMN_LABELS[column]}</span> : null}
      {reason ? <span>{reason}</span> : null}
    </Flex>
  );
}

function PipelineTable({ cards, loading }: { cards: readonly BoardCard[]; loading?: boolean | undefined }) {
  return (
    <Table
      label={NAV_ITEM_LABELS.pipeline}
      dense
      stickyHead
      loading={loading}
      cols={3}
      head={<Tr><Th>{NAV_ITEM_LABELS.candidates}</Th><Th>{NAV_ITEM_LABELS.requisitions}</Th><Th>{NAV_ITEM_LABELS.pipeline}</Th></Tr>}
    >
      {cards.map((card, index) => {
        const at = boardOf([card]).passed.length ? null : COLUMN_OF[card.stage === "held" ? (card.held_from_stage ?? "in_review") : card.stage];
        return (
          <Tr key={card.id} >
            <Td><TextLink href={`#requisitions/${card.rid}/candidates/${card.id}`}>{nameOf(card)}</TextLink></Td>
            <Td mono>{card.rid}</Td>
            <Td sub={at ? G6_TEMPLATES.inStage({ n: card.waiting_business_days }) : undefined}>
              <Flex gap={4} wrap>{at ? <span>{G6_COLUMN_LABELS[at]}</span> : <Pill tone="neutral">{HM_REVIEW_STATUS.passed}</Pill>}<Pills card={card} /></Flex>
            </Td>
          </Tr>
        );
      })}
    </Table>
  );
}
